import type { Module } from "vuex";

import type { DefaultApi } from "@/api";
import type { State as RootState } from "@/stores";

export type OnboardingTourFetchState = "idle" | "loading" | "loaded" | "failed";

export interface State {
  fetchState: OnboardingTourFetchState;
  pending: boolean;
  // A `?tour=1` forced run arms this so the tour (and its sample-card anchors) starts even when pending is false.
  forced: boolean;
  completing: boolean;
  completeError: boolean;
}

export const module: Module<State, RootState> = {
  namespaced: true,
  state: () => ({
    fetchState: "idle",
    pending: false,
    forced: false,
    completing: false,
    completeError: false,
  }),
  actions: {
    async fetch({ commit, state, dispatch }) {
      if (state.fetchState !== "idle") return;
      commit("setFetchState", "loading");
      try {
        const api: DefaultApi = await dispatch("getAuthorizedApi", null, {
          root: true,
        });
        const response = await api.getOnboardingTour();
        commit("setPending", response.data.pending);
        commit("setFetchState", "loaded");
      } catch (error) {
        // Fail quiet: the tour simply never starts this session.
        commit("setFetchState", "failed");
      }
    },
    async complete({ commit, state, dispatch }) {
      if (state.completing) return;
      commit("setCompleting", true);
      commit("setCompleteError", false);
      try {
        const api: DefaultApi = await dispatch("getAuthorizedApi", null, {
          root: true,
        });
        await api.postOnboardingTour();
        // Commit pending=false only after the 204: no optimistic update.
        commit("setPending", false);
        commit("setCompleteError", false);
      } catch (error) {
        commit("setCompleteError", true);
      } finally {
        commit("setCompleting", false);
      }
    },
  },
  mutations: {
    setFetchState(state, fetchState: OnboardingTourFetchState) {
      state.fetchState = fetchState;
    },
    setPending(state, pending: boolean) {
      state.pending = pending;
    },
    setForced(state, forced: boolean) {
      state.forced = forced;
    },
    setCompleting(state, completing: boolean) {
      state.completing = completing;
    },
    setCompleteError(state, completeError: boolean) {
      state.completeError = completeError;
    },
  },
};
